import PersonIcon from "@mui/icons-material/Person";
import Link from "assets/img/svg/link";
import Pen from "assets/img/svg/pen";
import Plus from "assets/img/svg/plus";
import { formatSocialProfileType, formatVatType } from "components/Beneficiar/utils";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import { formatAddress } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteRepresentative } from "store/beneficiaryStore";
import { useAppDispatch, useAppSelector } from "store/store";

import variables from "../../../assets/styles/_variables.module.scss";
import DeleteModal from "../DeleteModal";
import EmptyState from "../EmptyState";
import InformationGrid from "../InformationGrid";
import ModalBase from "../ModalBase";
import StyledButton from "../StyledButton";
import TextButton from "../TextButton";
import TitleSeparation from "../TitleSeparation";
import RepresentativeForm, { representativeRolesLabels } from "./RepresentativeForm";
import styles from "./style.module.scss";

interface IRepresentatives {
    /**
     * Representatives data from store (fetchAssociatedPersons())
     */
    representatives: IRepresentative[] | undefined;
    /**
     * Beneficiary data.
     */
    beneficiary: IBeneficiar;
    /**
     * Function to update representatives data.
     */
    setRepresentativeData: Dispatch<SetStateAction<IRepresentative[] | undefined>>;
}

/**
 * Representative tab on Beneficiary component.
 */
const Representatives = ({ representatives, beneficiary, setRepresentativeData }: IRepresentatives) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { associatedPersonsLoadStatus } = useAppSelector((state) => state.beneficiary);
    const dataLoading = ["idle", "pending"].includes(associatedPersonsLoadStatus);
    const { companyInfos, countryFeatures } = useCompanySettings();
    const usesDirectPrecount = countryFeatures.precountMode === "direct";

    const [modalType, setModalType] = useState("");
    const [formMode, setFormMode] = useState("");
    const [currentRepresentative, setCurrentRepresentative] = useState<IRepresentative>();

    /**
     * Updates representatives after a creation or update.
     */
    const handleRepresentativeUpdate = async (newRepresentative: IRepresentative) => {
        if (formMode === "new") {
            representatives && (await setRepresentativeData([...representatives, newRepresentative]));
            setFormMode("");
            setModalType("");
        } else {
            const newData = representatives?.map((item) => {
                if (item.id === newRepresentative.id) {
                    return newRepresentative;
                } else {
                    return item;
                }
            });

            await setRepresentativeData(newData);
            setFormMode("");
            setModalType("");
        }
    };

    /**
     * Opens form either for creation or update of a representative.
     */
    const handleOpeningForm = async (mode: "new" | "edit", representative?: IRepresentative) => {
        await setFormMode(mode);
        if (mode === "edit") {
            await setCurrentRepresentative(representative);
        }
        setModalType("representativeForm");
    };

    /**
     * Deletes representative.
     */
    const handleDeleteRepresentative = async () => {
        if (currentRepresentative) {
            const response = await dispatch(
                deleteRepresentative({ beneficiaryId: beneficiary.id, representativeId: currentRepresentative?.id }),
            );
            if (response.meta.requestStatus) {
                const newData = representatives?.filter((item) => item.id !== currentRepresentative.id);
                await setRepresentativeData(newData);
                setModalType("");
            }
        }
    };

    /**
     * Formats representative data to populate InformationGrid:
     */
    const formatRepresentativeData = (item: IRepresentative) => {
        const data: Record<string, any> = {};

        data[t("Representatives.TableRole")] =
            item.description === ""
                ? t("Representatives.NoRole")
                : ["heir", "author"].includes(item.description)
                  ? representativeRolesLabels()[item.description]
                  : item.description;

        data[t("Representatives.Address")] = formatAddress({
            street: item?.street,
            city: item?.city,
            zipCode: item?.zipCode,
            country: item?.country,
        });

        // If data is same as beneficiary, just display text with link:
        if (!item.computeFromRepresentative) {
            data[t("Representatives.Profile")] = {
                text: t("Representatives.ProfileAligned"),
                icon: <Link color={variables["info"]} />,
                style: { color: variables["info"] },
                tooltip: t("Representatives.Collapse.SameProfileTooltip"),
            };
        } else {
            // If different, display remaining fields: profile type, fiscal type, NIR and SIRET:
            if (usesDirectPrecount) {
                data[t("Representatives.Collapse.Precount")] =
                    t(countryFeatures.precount.options.find((o) => o.value === Number(item.precount))?.label ?? "") ||
                    "-";
            } else {
                data[t("Representatives.Collapse.SocialType")] = formatSocialProfileType({
                    item,
                    distributorType: companyInfos?.distributorType,
                    isCompanyBelgian: false,
                });
            }
            const countryVatLabel = countryFeatures.vat.options.find((o) => o.value === Number(item.vat))?.label;
            data[t("Representatives.Collapse.FiscalType")] = countryVatLabel
                ? t(countryVatLabel)
                : formatVatType(item.vat);
            data[t("Beneficiars.SecurityNumber")] = item?.socialSecurityNumber || "-";
            data[t("Beneficiars.Siret")] = item?.siretNumber || "-";
        }

        return data;
    };

    return (
        <div className={styles["representatives"]}>
            <TitleSeparation
                title={t("Representatives.SectionTitle")}
                description={t("Representatives.SectionSubtitle")}
                titleStyle={{ fontSize: "14px" }}
                style={{ width: "100%", marginBottom: "0.75rem" }}
            />
            <>
                {dataLoading ? (
                    <InformationGrid
                        title=""
                        titleIcon={<PersonIcon style={{ color: variables["foreground"], width: "16px" }} />}
                        data={{
                            [t("Representatives.TableRole")]: "",
                            [t("Representatives.Address")]: "",
                            [t("Representatives.Profile")]: "",
                        }}
                        isLoading
                        style={{ marginBottom: "1rem", width: "100%" }}
                    />
                ) : representatives?.length === 0 ? (
                    <EmptyState type="representatives" />
                ) : (
                    representatives?.map((representative) => {
                        return (
                            <InformationGrid
                                key={representative.id}
                                title={`${representative.surname ?? ""} ${representative.name ?? ""}`}
                                titleIcon={<PersonIcon style={{ color: variables["foreground"], width: "16px" }} />}
                                data={formatRepresentativeData(representative)}
                                style={{ marginBottom: "1rem", width: "100%" }}
                                headerActions={[
                                    <TextButton
                                        text={t("Representatives.Edit")}
                                        handleClick={() => handleOpeningForm("edit", representative)}
                                        key={`action-1`}
                                        icon={<Pen width={14} />}
                                        underlineOnHover
                                        style={{ fontSize: "0.8rem", color: variables["foreground"] }}
                                    />,
                                ]}
                            />
                        );
                    })
                )}
                <StyledButton
                    sx={{ gap: "8px", padding: "4px 16px", background: "white", margin: "0", display: "flex" }}
                    onClick={() => handleOpeningForm("new")}
                    buttonType="secondary"
                    className={styles["add-representative"]}
                >
                    <Plus />
                    {t("Beneficiars.AddRepresentative")}
                </StyledButton>
            </>
            <ModalBase
                open={modalType === "representativeForm"}
                onClose={() => {
                    setModalType("");
                }}
                modalTitle={
                    formMode === "new" ? t("Representatives.Form.TitleNew") : t("Representatives.Form.TitleEdit")
                }
                modalSubtitle={t("Representatives.Form.Subtitle")}
                widthTransition={{ width: "590px", active: true }}
            >
                <RepresentativeForm
                    representative={currentRepresentative}
                    beneficiary={beneficiary}
                    formMode={formMode}
                    onClose={handleRepresentativeUpdate}
                    setModalType={setModalType}
                />
            </ModalBase>
            <DeleteModal
                open={modalType === "delete"}
                onClose={() => setModalType("")}
                elementTitle={t("Representatives.DeleteElement", {
                    name: `${currentRepresentative?.surname.toUpperCase()} ${currentRepresentative?.name ?? ""}`,
                })}
                onDelete={handleDeleteRepresentative}
                description={t("Representatives.DeleteDescription")}
            />
        </div>
    );
};

export default Representatives;
